import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    isLoading: false,
    isLoadingMore: false,
    isDetailLoading: false,
    isRecalculating: false,
    recalculateError: null,
    results: [],
    totalRecords: 0,
    page: 1,
    pageSize: 25,
    selectedDetail: null,
    selectedDetailId: null,
    deepResults: {}, // { [invoiceId]: { overall, issueCount, checks, ... } }
    verifyingId: null, // currently being deep-verified
    queuedIds: [], // ids waiting in the deep-verify queue
    error: null,
    filters: {
        from: null,
        to: null,
        entityId: null,
        verificationStatus: 'all',
        showSent: false,
    },
};

const slice = createSlice({
    name: 'weeklyInvoiceVerification',
    initialState,
    reducers: {
        fetchList: state => {
            state.isLoading = true;
            state.error = null;
        },
        listFetched: (state, { payload }) => {
            state.results = payload.results || [];
            state.totalRecords = payload.totalRecords || 0;
            state.page = payload.page || 1;
            state.pageSize = payload.pageSize || 25;
            state.isLoading = false;
            // reset deep results & queue for the new page
            state.deepResults = {};
            state.verifyingId = null;
            state.queuedIds = (payload.results || [])
                .map(r => r.invoice && r.invoice._id)
                .filter(Boolean);
        },
        listFetchFailed: (state, { payload }) => {
            state.isLoading = false;
            state.error = payload || 'Failed to load weekly invoices';
        },
        fetchMore: state => {
            state.isLoadingMore = true;
            state.error = null;
        },
        moreFetched: (state, { payload }) => {
            const newResults = payload.results || [];
            const existingIds = new Set(
                state.results
                    .map(r => r.invoice && r.invoice._id)
                    .filter(Boolean),
            );
            const appended = newResults.filter(
                r => !existingIds.has(r.invoice && r.invoice._id),
            );
            state.results = state.results.concat(appended);
            state.totalRecords = payload.totalRecords ?? state.totalRecords;
            state.page = payload.page || state.page;
            state.pageSize = payload.pageSize || state.pageSize;
            state.isLoadingMore = false;
            const newQueued = appended
                .map(r => r.invoice && r.invoice._id)
                .filter(id => id && !state.deepResults[id]);
            state.queuedIds = state.queuedIds.concat(newQueued);
        },
        moreFetchFailed: (state, { payload }) => {
            state.isLoadingMore = false;
            state.error = payload || 'Failed to load more invoices';
        },
        runDeepQueue: () => {
            // saga-only signal; no state change
        },
        startDeepVerify: (state, { payload }) => {
            state.verifyingId = payload;
            state.queuedIds = state.queuedIds.filter(id => id !== payload);
        },
        deepResultReceived: (state, { payload }) => {
            const { id, result } = payload;
            state.deepResults[id] = result;
            if (state.verifyingId === id) state.verifyingId = null;
            // if the drawer is open on this id, also push it into selectedDetail
            if (state.selectedDetailId === id) {
                state.selectedDetail = result;
                state.isDetailLoading = false;
            }
        },
        deepResultFailed: (state, { payload }) => {
            const { id, error } = payload;
            state.deepResults[id] = { overall: 'fail', issueCount: 1, error };
            if (state.verifyingId === id) state.verifyingId = null;
        },
        fetchDetail: (state, { payload }) => {
            state.selectedDetailId = payload;
            // serve from cache if we already have it
            if (state.deepResults[payload]) {
                state.selectedDetail = state.deepResults[payload];
                state.isDetailLoading = false;
            } else {
                state.isDetailLoading = true;
            }
        },
        detailFetched: (state, { payload }) => {
            state.selectedDetail = payload;
            state.isDetailLoading = false;
            const id = payload && payload.invoice && payload.invoice._id;
            if (id) {
                state.deepResults[id] = payload;
                if (state.verifyingId === id) state.verifyingId = null;
            }
        },
        detailFetchFailed: state => {
            state.isDetailLoading = false;
        },
        clearDetail: state => {
            state.selectedDetail = null;
            state.selectedDetailId = null;
        },
        recalculateInvoice: (state, { payload }) => {
            state.isRecalculating = true;
            state.recalculateError = null;
            state.selectedDetailId = payload;
        },
        recalculateInvoiceSucceeded: (state, { payload }) => {
            state.isRecalculating = false;
            state.recalculateError = null;
            const id = payload && payload.invoice && payload.invoice._id;
            if (id) {
                state.deepResults[id] = payload;
                if (state.selectedDetailId === id) {
                    state.selectedDetail = payload;
                }
            }
        },
        recalculateInvoiceFailed: (state, { payload }) => {
            state.isRecalculating = false;
            state.recalculateError = payload || 'Failed to recalculate invoice';
        },
        setFilters: (state, { payload }) => {
            state.filters = { ...state.filters, ...payload };
        },
        setPage: (state, { payload }) => {
            state.page = payload;
        },
    },
});

export const {
    fetchList,
    listFetched,
    listFetchFailed,
    fetchMore,
    moreFetched,
    moreFetchFailed,
    runDeepQueue,
    startDeepVerify,
    deepResultReceived,
    deepResultFailed,
    fetchDetail,
    detailFetched,
    detailFetchFailed,
    clearDetail,
    recalculateInvoice,
    recalculateInvoiceSucceeded,
    recalculateInvoiceFailed,
    setFilters,
    setPage,
} = slice.actions;

export default slice.reducer;

export const selectVerificationState = state => state.weeklyInvoiceVerification;
